import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import { useSeo } from "@/hooks/useSeo";
import { ShieldCheck } from "lucide-react";

const H2 = ({ children }) => (
  <h2 className="font-display text-2xl sm:text-3xl font-semibold text-emerald-900 mt-12 mb-4 scroll-mt-24">
    {children}
  </h2>
);
const H3 = ({ children }) => (
  <h3 className="font-display text-lg font-semibold text-emerald-900 mt-8 mb-3">{children}</h3>
);
const P = ({ children }) => <p className="text-stone-700 leading-relaxed mb-4">{children}</p>;
const UL = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1.5 text-stone-700 leading-relaxed mb-4">
    {items.map((it, i) => (
      <li key={i}>{it}</li>
    ))}
  </ul>
);
const HR = () => <hr className="border-stone-100 my-2" />;

export default function Datenschutz() {
  useSeo(
    "Datenschutzerklärung – LAIzyones",
    "Datenschutzerklärung von LAIzyones: Informationen zur Verarbeitung personenbezogener Daten bei Nutzung unserer Website und der Softwarelösung LAIzyDocu (DSGVO-konform).",
  );

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="datenschutz-page">
      <SiteNav />

      <section className="max-w-3xl mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-6">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 text-emerald-800 text-sm font-medium">
          <ShieldCheck className="w-4 h-4" /> Rechtliches
        </span>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold text-emerald-900 leading-[1.05] mt-6">
          Datenschutzerklärung
        </h1>
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-10 pb-20">
        <div className="bg-white rounded-[2rem] border border-stone-100 shadow-[0_20px_60px_rgb(0,0,0,0.05)] p-7 md:p-12 text-base">

          <H2>1. Verantwortlicher</H2>
          <P>
            Verantwortlicher für diese Website sowie die Bereitstellung der nachfolgend beschriebenen
            Softwarelösung ist:
          </P>
          <div className="rounded-2xl bg-stone-50 border border-stone-100 p-6 mb-4 leading-relaxed text-stone-700">
            <p className="font-semibold text-emerald-900">LAIzyones</p>
            <p>Einzelunternehmen</p>
            <p className="mt-3"><span className="font-semibold">Inhaber:</span> David Ehrle</p>
            <p className="mt-3">
              Ferdinand-Lentjes-Straße 32<br />
              39167 Wellen<br />
              Deutschland
            </p>
            <p className="mt-3">
              Telefon: 0179 7615911<br />
              E-Mail:{" "}
              <a href="mailto:info@laizyones.de" className="text-emerald-700 underline">
                info@laizyones.de
              </a>
              <br />
              Website:{" "}
              <a href="https://laizyones.de" target="_blank" rel="noreferrer" className="text-emerald-700 underline">
                https://laizyones.de
              </a>
            </p>
          </div>
          <P>Verantwortlich für den Inhalt gemäß § 55 Abs. 2 MStV:</P>
          <P>
            David Ehrle<br />
            Ferdinand-Lentjes-Straße 32<br />
            39167 Wellen
          </P>
          <P>Operative Projektleitung: Philipp-Arsim Nuraj</P>
          <P>
            Steuernummer: 105/215/02350<br />
            Finanzamt Magdeburg
          </P>
          <P>Wirtschafts-Identifikationsnummer: DE459919567</P>
          <HR />

          <H2>2. Allgemeine Hinweise zum Datenschutz</H2>
          <P>
            Der Schutz personenbezogener Daten hat für LAIzyones einen hohen Stellenwert. Wir verarbeiten
            personenbezogene Daten ausschließlich im Einklang mit den geltenden datenschutzrechtlichen
            Bestimmungen, insbesondere der Datenschutz-Grundverordnung (DSGVO), dem Bundesdatenschutzgesetz
            (BDSG) sowie sonstigen anwendbaren Datenschutzvorschriften.
          </P>
          <P>
            Diese Datenschutzerklärung informiert über Art, Umfang und Zweck der Verarbeitung personenbezogener
            Daten bei der Nutzung unserer Website sowie unserer Softwarelösung zur digitalen Erfassung und
            Verwaltung von Datenschutzerklärungen, Einwilligungen und Behandlungsverträgen.
          </P>
          <HR />

          <H2>3. Angaben zur Software</H2>
          <P>Die angebotene Software ist ein Produkt und eine Marke von LAIzyones.</P>
          <P>Die Software ermöglicht insbesondere:</P>
          <UL items={[
            "digitale Erfassung von Datenschutzerklärungen",
            "digitale Erfassung von Behandlungsverträgen",
            "elektronische Unterzeichnung von Dokumenten",
            "Verwaltung von Dokumenten durch Praxen",
            "PDF-Erstellung und PDF-Archivierung",
            "Dokumentenverwaltung innerhalb eines geschützten Administrationsbereiches",
          ]} />
          <P>
            Patienten können Dokumente über bereitgestellte QR-Codes oder digitale Endgeräte der jeweiligen
            Praxis aufrufen, ausfüllen und elektronisch unterzeichnen.
          </P>
          <HR />

          <H2>4. Rollen nach der DSGVO</H2>
          <P>Bei der Nutzung der Software gilt grundsätzlich:</P>
          <P><strong>Die jeweilige Praxis ist Verantwortlicher gemäß Art. 4 Nr. 7 DSGVO.</strong></P>
          <P><strong>LAIzyones handelt als Auftragsverarbeiter gemäß Art. 28 DSGVO.</strong></P>
          <P>
            Personenbezogene Daten werden ausschließlich im Auftrag und nach Weisung der jeweiligen Praxis
            verarbeitet.
          </P>
          <P>
            Für die Nutzung der Software wird zwischen der Praxis und LAIzyones ein Auftragsverarbeitungsvertrag
            (AVV) gemäß Art. 28 DSGVO geschlossen.
          </P>
          <HR />

          <H2>5. Welche Daten werden verarbeitet?</H2>
          <H3>5.1 Daten von Praxisnutzern</H3>
          <P>Bei der Registrierung und Nutzung der Software können insbesondere folgende Daten verarbeitet werden:</P>
          <UL items={[
            "Name",
            "E-Mail-Adresse",
            "Benutzername",
            "Unternehmens- oder Praxisdaten",
            "Benutzerrolle",
            "Anmeldedaten",
            "Protokolldaten",
            "Sicherheitsdaten",
          ]} />
          <H3>5.2 Daten von Patienten</H3>
          <P>Je nach Nutzung der Software können folgende personenbezogene Daten verarbeitet werden:</P>
          <UL items={[
            "Vorname",
            "Nachname",
            "Geburtsdatum",
            "Kontaktdaten",
            "digitale Unterschrift",
            "Datum und Uhrzeit der Unterzeichnung",
            "Dokumentenversion",
            "Dokumenten-ID",
            "Datenschutzerklärungen",
            "Behandlungsverträge",
          ]} />
          <P>
            Aktuell ist die Software primär auf die Verwaltung von Datenschutzerklärungen und
            Behandlungsverträgen ausgelegt.
          </P>
          <HR />

          <H2>6. Zwecke der Verarbeitung</H2>
          <P>Die Verarbeitung erfolgt insbesondere zu folgenden Zwecken:</P>
          <UL items={[
            "Bereitstellung der Software",
            "Verwaltung von Benutzerkonten",
            "Authentifizierung von Nutzern",
            "Durchführung von Zwei-Faktor-Authentifizierung (2FA)",
            "digitale Dokumentenerfassung",
            "Erstellung von PDF-Dokumenten",
            "Nachweisführung über abgegebene Erklärungen",
            "Dokumentation elektronischer Signaturen",
            "Sicherstellung der Systemsicherheit",
            "Missbrauchsverhinderung",
            "Fehleranalyse",
            "Erfüllung gesetzlicher Aufbewahrungs- und Nachweispflichten",
          ]} />
          <HR />

          <H2>7. Rechtsgrundlagen der Verarbeitung</H2>
          <P>Die Verarbeitung personenbezogener Daten erfolgt insbesondere auf Grundlage von:</P>
          <UL items={[
            "Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)",
            "Art. 6 Abs. 1 lit. c DSGVO (rechtliche Verpflichtung)",
            "Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse)",
          ]} />
          <P>
            Soweit Einwilligungen erforderlich sind, erfolgt die Verarbeitung auf Grundlage von
            Art. 6 Abs. 1 lit. a DSGVO.
          </P>
          <HR />

          <H2>8. Hosting und Datenspeicherung</H2>
          <P>Zur Bereitstellung unserer Software nutzen wir MongoDB Atlas als Datenbankplattform.</P>
          <P>
            Die Speicherung der Daten erfolgt in europäischen Hosting-Regionen. Die Infrastruktur wird
            entsprechend der gewählten Konfiguration innerhalb der Europäischen Union betrieben.
          </P>
          <P>
            Mit MongoDB bestehen vertragliche Vereinbarungen zur Auftragsverarbeitung gemäß Art. 28 DSGVO
            (Data Processing Agreement – DPA).
          </P>
          <P>Weitere Informationen hierzu finden sich in den Datenschutz- und Vertragsunterlagen von MongoDB.</P>
          <HR />

          <H2>9. Technische und organisatorische Maßnahmen</H2>
          <P>
            Zum Schutz personenbezogener Daten setzen wir angemessene technische und organisatorische Maßnahmen
            gemäß Art. 32 DSGVO ein.
          </P>
          <P>Hierzu gehören insbesondere:</P>
          <UL items={[
            "TLS-verschlüsselte Datenübertragung",
            "Zwei-Faktor-Authentifizierung (2FA)",
            "rollenbasierte Zugriffskontrolle",
            "Mandantentrennung zwischen einzelnen Praxen",
            "Zugriffsbeschränkungen auf Datenbestände",
            "Protokollierung sicherheitsrelevanter Aktionen",
            "Audit-Logs",
            "Änderungsprotokolle",
            "Soft-Delete-Verfahren",
            "regelmäßige Datensicherungen",
            "Schutz vor unbefugten Zugriffen",
            "Passwort-Hashing nach aktuellem Stand der Technik",
          ]} />
          <P>Jede Praxis kann ausschließlich auf die ihr zugeordneten Daten zugreifen.</P>
          <P>Ein Zugriff auf Daten anderer Praxen ist technisch ausgeschlossen.</P>
          <HR />

          <H2>10. Elektronische Signaturen</H2>
          <P>Die Software ermöglicht die elektronische Unterzeichnung von Dokumenten.</P>
          <P>Zur Nachweisbarkeit können insbesondere gespeichert werden:</P>
          <UL items={[
            "elektronische Unterschrift",
            "Zeitstempel",
            "Dokumentenversion",
            "Dokumenten-ID",
            "Name der unterzeichnenden Person",
          ]} />
          <P>
            Diese Informationen dienen der Dokumentation und Nachweisbarkeit der abgegebenen Erklärung.
          </P>
          <HR />

          <H2>11. Protokollierung und Audit-Logs</H2>
          <P>
            Zur Gewährleistung von Sicherheit, Integrität und Nachvollziehbarkeit werden bestimmte Aktionen
            innerhalb des Systems protokolliert.
          </P>
          <P>Hierzu können insbesondere gehören:</P>
          <UL items={[
            "Anmeldungen",
            "Abmeldungen",
            "Änderungen von Benutzerrechten",
            "Dokumentenzugriffe",
            "Dokumentendownloads",
            "Löschvorgänge",
            "Einstellungen im Administrationsbereich",
            "Sicherheitsereignisse",
          ]} />
          <P>Die Protokollierung erfolgt ausschließlich zu Sicherheits-, Nachweis- und Administrationszwecken.</P>
          <HR />

          <H2>12. Dokumentendownloads</H2>
          <P>Autorisierte Benutzer einer Praxis können Dokumente als PDF herunterladen.</P>
          <P>
            Downloads erfolgen ausschließlich innerhalb der durch die jeweilige Praxis vergebenen Berechtigungen.
          </P>
          <P>
            Dokumentendownloads können protokolliert werden, um die Nachvollziehbarkeit von Zugriffen
            sicherzustellen.
          </P>
          <HR />

          <H2>13. Testzugang</H2>
          <P>Wir können Interessenten einen kostenfreien Testzugang zur Verfügung stellen.</P>
          <P>
            Während der Testphase können dieselben personenbezogenen Daten verarbeitet werden wie im regulären
            Betrieb.
          </P>
          <P>
            Die Testversion dient ausschließlich der Evaluierung der Software und kann Funktionsänderungen,
            Einschränkungen oder Anpassungen unterliegen.
          </P>
          <HR />

          <H2>14. Speicherdauer</H2>
          <P>
            Personenbezogene Daten werden nur solange gespeichert, wie dies zur Erfüllung der jeweiligen Zwecke
            erforderlich ist oder gesetzliche Aufbewahrungspflichten bestehen.
          </P>
          <P>
            Gelöschte Datensätze können zunächst technisch als gelöscht markiert („Soft Delete“) werden, bevor
            eine endgültige Entfernung erfolgt.
          </P>
          <P>
            Nach Ablauf gesetzlicher Aufbewahrungsfristen oder auf entsprechende Weisung der verantwortlichen
            Praxis werden Daten gelöscht oder anonymisiert.
          </P>
          <HR />

          <H2>15. Weitergabe von Daten</H2>
          <P>Eine Weitergabe personenbezogener Daten erfolgt ausschließlich:</P>
          <UL items={[
            "auf Weisung der verantwortlichen Praxis,",
            "zur Erfüllung gesetzlicher Verpflichtungen,",
            "an vertraglich gebundene Auftragsverarbeiter,",
            "soweit dies zur Bereitstellung der Software erforderlich ist.",
          ]} />
          <P>Eine Weitergabe zu Werbezwecken erfolgt nicht.</P>
          <HR />

          <H2>16. Cookies und Tracking-Technologien</H2>
          <P>
            Aktuell verwenden wir keine Analyse-, Tracking- oder Marketingtools zur Auswertung des
            Nutzerverhaltens innerhalb der Software.
          </P>
          <P>
            Soweit technisch erforderliche Cookies eingesetzt werden, dienen diese ausschließlich dem Betrieb
            und der Sicherheit der Plattform.
          </P>
          <HR />

          <H2>17. Rechte betroffener Personen</H2>
          <P>
            Betroffene Personen haben im Rahmen der gesetzlichen Voraussetzungen insbesondere folgende Rechte:
          </P>
          <UL items={[
            "Auskunft gemäß Art. 15 DSGVO",
            "Berichtigung gemäß Art. 16 DSGVO",
            "Löschung gemäß Art. 17 DSGVO",
            "Einschränkung der Verarbeitung gemäß Art. 18 DSGVO",
            "Datenübertragbarkeit gemäß Art. 20 DSGVO",
            "Widerspruch gemäß Art. 21 DSGVO",
            "Widerruf erteilter Einwilligungen mit Wirkung für die Zukunft",
          ]} />
          <P>Anfragen können an die oben genannten Kontaktdaten gerichtet werden.</P>
          <HR />

          <H2>18. Beschwerderecht</H2>
          <P>
            Betroffene Personen haben das Recht, sich bei einer zuständigen Datenschutzaufsichtsbehörde über die
            Verarbeitung ihrer personenbezogenen Daten zu beschweren.
          </P>
          <P>
            Für Unternehmen mit Sitz in Sachsen-Anhalt ist grundsätzlich der Landesbeauftragte für den
            Datenschutz Sachsen-Anhalt zuständig.
          </P>
          <HR />

          <H2>19. Änderungen dieser Datenschutzerklärung</H2>
          <P>
            Wir behalten uns vor, diese Datenschutzerklärung anzupassen, wenn dies aufgrund technischer,
            organisatorischer oder rechtlicher Änderungen erforderlich wird.
          </P>
          <P>Es gilt jeweils die zum Zeitpunkt der Nutzung veröffentlichte Fassung.</P>
          <p className="text-sm text-stone-500 mt-8">Stand: Juni 2026</p>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
